import { Box, Skeleton } from '@mui/material';
import React, { useMemo, useState, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  ReactFlowProvider,
  Node,
  Edge,
  OnNodesChange,
  applyNodeChanges,
  OnEdgesChange,
  applyEdgeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { convertOpenFlowToReactFlow } from '../../utils/OpenFlowToReactFlow';
import { getNodeTypes } from '../../components/Windmill/CustomNodes';
import { getEdgeTypes } from '../../components/Windmill/CustomEdges';
import NodeDetailsSidebar from '../../components/Windmill/NodeDetailsSidebar';
import { FlowNodeData } from '../../components/Windmill/CustomNodes';

interface FlowAreaProps {
  flowName: string;
  flowData: any;
}

const FlowAreaContent: React.FC<FlowAreaProps> = ({ flowData }) => {
  // State for node details sidebar
  const [selectedNode, setSelectedNode] = useState<FlowNodeData | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Convert OpenFlow data to React Flow format
  const initialFlow = useMemo(() => {
    if (!flowData?.value?.modules) {
      return { nodes: [], edges: [] };
    }
    return convertOpenFlowToReactFlow(flowData);
  }, [flowData]);

  // State for nodes and edges with dragging support
  const [nodes, setNodes] = useState<Node[]>(initialFlow.nodes);
  const [edges, setEdges] = useState<Edge[]>(initialFlow.edges);

  // Handle node click
  const handleNodeClick = useCallback(
    (nodeId: string, nodeData: FlowNodeData) => {
      setSelectedNode(nodeData);
      setSelectedNodeId(nodeId);
      setSidebarOpen(true);
    },
    []
  );

  // Handle sidebar close
  const handleSidebarClose = useCallback(() => {
    setSidebarOpen(false);
    setSelectedNode(null);
    setSelectedNodeId(null);
  }, []);

  // Update nodes when initial flow changes
  React.useEffect(() => {
    const nodesWithClickHandlers = initialFlow.nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        onNodeClick: handleNodeClick,
        // Add flow data to input and trigger nodes
        flowData:
          node.type === 'inputNode' || node.type === 'triggerNode'
            ? flowData
            : undefined,
      },
    }));
    setNodes(nodesWithClickHandlers);
    setEdges(initialFlow.edges);
  }, [initialFlow, flowData, handleNodeClick]); // Add handleNodeClick to dependencies

  const nodeTypes = useMemo(() => getNodeTypes(), []);
  const edgeTypes = useMemo(() => getEdgeTypes(), []);

  // Handle node changes (dragging, selection, etc.)
  const onNodesChange: OnNodesChange = useCallback(changes => {
    setNodes(nds => applyNodeChanges(changes, nds));
  }, []);

  // Handle edge changes
  const onEdgesChange: OnEdgesChange = useCallback(changes => {
    setEdges(eds => applyEdgeChanges(changes, eds));
  }, []);

  if (!flowData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 100px)',
        width: '100%',
        position: 'relative',
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        style={{ backgroundColor: '#f8f9fa' }}
        proOptions={{ hideAttribution: true }}
        panOnScroll={true}
        panOnScrollSpeed={1}
        panOnDrag={false} // Disable pan on drag to allow node dragging
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={true}
        defaultEdgeOptions={{
          type: 'custom',
        }}
        nodesDraggable={true} // Enable node dragging
        nodesConnectable={true}
        elementsSelectable={true}
        selectNodesOnDrag={false} // Prevent selection when dragging
        multiSelectionKeyCode="Shift" // Hold Shift for multi-selection
        deleteKeyCode="Delete" // Delete key to remove selected elements
      >
        <Background color="#e0e0e0" gap={20} />
        <Controls />
      </ReactFlow>

      {/* Node Details Sidebar */}
      <NodeDetailsSidebar
        open={sidebarOpen}
        onClose={handleSidebarClose}
        nodeData={selectedNode}
        nodeId={selectedNodeId}
      />
    </Box>
  );
};

const FlowArea: React.FC<FlowAreaProps> = ({ flowName, flowData }) => {
  return (
    <ReactFlowProvider>
      <FlowAreaContent flowName={flowName} flowData={flowData} />
    </ReactFlowProvider>
  );
};

export default FlowArea;
